import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import getPrettyNumber, { getPrettyTimeAgo } from '../../utils/Number/NumberUtils';
import styled, { css } from 'components/_UI/designSystem';
import { StyledUserName, StyledSecondaryName } from './Parts/nativeProfileLockup/styledComponents';
import NativeText from './Parts/nativeText/NativeText';
import { StyledNativeText } from './Parts/nativeText/styledComponents';
import NativeMedia from './Parts/nativeMedia/NativeMedia';
import { StyledMedia } from './Parts/nativeMedia/styledComponents';
import {
    useNativePost,
    getNativeMediaOverrides,
    baseStyles,
    getEntityStyles,
    StyledMeta,
    getNativeColor,
} from './Helpers';
import { NativePostProps, StyledPostProps } from './DefaultPost';
import NativeIcon from './Bits/NativeIcon';
import NativeFlex from './Bits/NativeFlex';
import { usePostClickableEntities } from '@sprinklr/display-builder/hooks/usePostClickableEntities';
import NativeProfileLockup from './Parts/nativeProfileLockup/NativeProfileLockup';

const colors = {
    blue: '#013469',
    lightGray: 'rgb(153, 153, 153)',
    black: '#000',
};

const theme = {
    light: {
        link: colors.blue,
        text: colors.black,
        secondary: colors.lightGray,
    },
    dark: {
        link: colors.blue,
        text: colors.lightGray,
        secondary: colors.lightGray,
    },
};

const StyledSmallText = styled.span`
    ${({ colorMode }: StyledPostProps) =>
        css`
            color: ${getNativeColor(theme, colorMode, 'text')};
        `}
    font-size: .7em;
    span {
        font-weight: bold;
    }
`;

const StyledLightText = styled.span`
    ${({ colorMode }: StyledPostProps) =>
        css`
            color: ${getNativeColor(theme, colorMode, 'secondary')};
        `}
    font-size: .7em;
    text-transform: uppercase;
`;

const StyledText = styled.span`
    font-size: 0.85em;
    ${({ colorMode }: StyledPostProps) =>
        css`
            color: ${getNativeColor(theme, colorMode, 'secondary')};
        `}
`;

const StyledPost: any = styled(NativeFlex)`
    ${(props: NativePostProps) => baseStyles(props.options)}
    ${({ ratio }: any) => getNativeMediaOverrides(ratio > 2.3 ? 40 : 60, 60)}

    ${({ colorMode }: StyledPostProps) => getEntityStyles(getNativeColor(theme, colorMode, 'link'))}

    ${StyledMedia} {
        border-radius: 0;
    }
    ${StyledUserName} {
    }
    ${StyledSecondaryName} {
    }
    ${StyledNativeText} {
    }
    ${({ colorMode }: StyledPostProps) =>
        css`
            .extra_post_data_value {
                width: auto;
                color: ${getNativeColor(theme, colorMode, 'secondary')};
            }
        `}
`;

const NativeFlexEngagement = styled(NativeFlex)`
    ${NativeIcon} {
    }
`;

const InstagramPost: React.FC<NativePostProps> = ({
    post,
    playerInterval,
    options,
    extraPostDataTags,
}) => {
    const {
        lineClamp,
        maxCharacters,
        colorMode,
        removeUrls,
        emoji,
        showExpandedEntityUrls,
        clickableUrls,
    } = options;

    const {
        postRef,
        screenName,
        profileImgUrl,
        images,
        videos,
        snCreatedTime,
        snStats,
        unique,
        snType,
        expandedTextEntities,
        location,
        hasMedia,
        text,
        rtl,
        language,
        orientation,
        ratio,
        profilePermalink,
        permalink,
    } = useNativePost(post, showExpandedEntityUrls);

    const { handlePermalinkClick } = usePostClickableEntities({
        permalink,
        clickableUrls,
        profilePermalink,
    });

    const timeAgo = useMemo(
        () => (
            <StyledLightText colorMode={colorMode} onClick={handlePermalinkClick}>
                {getPrettyTimeAgo(snCreatedTime)}
            </StyledLightText>
        ),
        [colorMode, snCreatedTime, handlePermalinkClick]
    );

    if (!post) {
        return <div key={unique} />;
    }

    const { value, suffix } = getPrettyNumber(snStats.numLikes, 2, false);
    const { value: commentsValue, suffix: commentsSuffix } = getPrettyNumber(
        snStats.numComments,
        2,
        false
    );

    const likes = !!snStats.numLikes && `${value}${suffix}`;
    const comments = !!snStats.numComments && `${commentsValue}${commentsSuffix}`;

    return (
        <StyledPost
            options={options}
            key={unique}
            ref={postRef}
            hasMedia={hasMedia}
            ratio={ratio}
            vertical={orientation === 'vertical'}
            orientation={orientation}>
            <>
                {orientation === 'vertical' && (
                    <>
                        {(profileImgUrl || screenName || snType || (location && location.text)) && (
                            <NativeProfileLockup
                                colorMode={colorMode}
                                pad
                                primary={screenName}
                                secondary={
                                    location &&
                                    location.text &&
                                    location.text.toLowerCase() !== 'unknown' &&
                                    location.text
                                }
                                snType={snType}
                                profileImgUrl={profileImgUrl}
                                clickableUrls={clickableUrls}
                                profilePermalink={profilePermalink}
                                permalink={permalink}
                                secondaryType='location'
                            />
                        )}
                        {hasMedia && (
                            <NativeMedia
                                snType={snType}
                                playerInterval={playerInterval}
                                images={images}
                                videos={videos}
                            />
                        )}
                        <NativeFlex pad vertical gap='m'>
                            <NativeFlexEngagement gap='m' noGrow>
                                <NativeIcon size='l' value='native_twitter_like' />
                                <NativeIcon size='l' value='native_twitter_comment' />
                            </NativeFlexEngagement>

                            {snStats && likes && (
                                <StyledSmallText colorMode={colorMode}>
                                    Liked by <span>{likes} people</span>
                                </StyledSmallText>
                            )}
                            <NativeText
                                removeUrls={removeUrls}
                                emoji={emoji}
                                colorMode={colorMode}
                                hasMedia={hasMedia}
                                lineClamp={lineClamp}
                                maxCharacters={maxCharacters}
                                channel={snType}
                                text={text}
                                rtl={rtl}
                                language={language}
                                textEntities={expandedTextEntities && expandedTextEntities.message}
                                clickableUrls={clickableUrls}
                            />

                            <NativeFlex vertical bottom gap='s'>
                                {comments && (
                                    <StyledText colorMode={colorMode}>
                                        {comments} Comments
                                    </StyledText>
                                )}
                                {timeAgo}
                                {extraPostDataTags}
                            </NativeFlex>
                        </NativeFlex>
                    </>
                )}

                {orientation === 'horizontal' && (
                    <>
                        {hasMedia && (
                            <NativeMedia
                                snType={snType}
                                playerInterval={playerInterval}
                                images={images}
                                videos={videos}
                            />
                        )}
                        <StyledMeta pad vertical gap='m'>
                            {(profileImgUrl ||
                                screenName ||
                                snType ||
                                (location && location.text)) && (
                                <NativeProfileLockup
                                    colorMode={colorMode}
                                    primary={screenName}
                                    secondary={
                                        location &&
                                        location.text &&
                                        location.text.toLowerCase() !== 'unknown' &&
                                        location.text
                                    }
                                    snType={snType}
                                    profileImgUrl={profileImgUrl}
                                    clickableUrls={clickableUrls}
                                    profilePermalink={profilePermalink}
                                    permalink={permalink}
                                    secondaryType='location'
                                />
                            )}

                            <NativeFlex vertical between gap='m'>
                                <NativeFlex vertical gap='m'>
                                    <NativeText
                                        removeUrls={removeUrls}
                                        emoji={emoji}
                                        colorMode={colorMode}
                                        hasMedia={hasMedia}
                                        lineClamp={lineClamp}
                                        maxCharacters={maxCharacters}
                                        channel={snType}
                                        text={text}
                                        textEntities={
                                            expandedTextEntities && expandedTextEntities.message
                                        }
                                        clickableUrls={clickableUrls}
                                    />
                                    {comments && (
                                        <StyledText colorMode={colorMode}>
                                            {comments} Comments
                                        </StyledText>
                                    )}
                                </NativeFlex>
                                <NativeFlex vertical bottom gap='s'>
                                    <NativeFlexEngagement gap='m' noGrow>
                                        <NativeIcon size='m' value='native_twitter_like' />
                                        <NativeIcon size='m' value='native_twitter_comment' />
                                    </NativeFlexEngagement>

                                    {snStats && likes && (
                                        <StyledSmallText colorMode={colorMode}>
                                            Liked by <span>{likes} people</span>
                                        </StyledSmallText>
                                    )}

                                    {extraPostDataTags ? (
                                        <NativeFlex bottom between noGrow>
                                            {timeAgo}
                                            {extraPostDataTags}
                                        </NativeFlex>
                                    ) : (
                                        timeAgo
                                    )}
                                </NativeFlex>
                            </NativeFlex>
                        </StyledMeta>
                    </>
                )}
            </>
        </StyledPost>
    );
};

InstagramPost.displayName = 'InstagramPost';
export default observer(InstagramPost);
